// Story E6.3.2 — PresenceContext: hook global montado no Layout pós-login.
//
// Responsabilidade:
//   1. Hydrate inicial: GET /presence/users?include_company=true
//   2. Abrir WS /ws/presence?token=<jwt> 1× para a sessão inteira
//   3. Heartbeat 30s (TTL Redis 60s no backend; margem 2× absorve jitter)
//   4. Visibility auto-away (aba hidden → away; visible → online). O heartbeat leva SÓ
//      o automático: ele é o PEDIDO desta aba, não a escolha da pessoa.
//   5. Receber broadcasts `presence_update` → atualizar Map<userId, entry>
//   6. Expor:
//        - lookupStatus(userId): retorna `offline` se ausente (sem key Redis)
//        - selfStatus: status do próprio user
//        - setStatus(status): grava a escolha no servidor (PUT /presence/me/status) e
//          manda um heartbeat; rejeita se o PUT falhar (online|away|busy). Os PUTs
//          correm em SÉRIE: um só começa depois que o anterior terminou
//        - wsConnected: bool, pra UI degradar quando offline-da-rede
//
// O status MANUAL (Ausente/Ocupado) mora no servidor (`users.status_manual`) e vale até
// a pessoa mudar: todas as abas e aparelhos, recarregar, fechar o navegador. Quem o
// aplica por cima do heartbeat é o servidor — por isso aqui não há mais "manual sticky"
// numa ref: ela só valia nesta aba, e uma aba nova (ou um recarregamento) a derrubava.
// "Online" no menu = voltar ao automático (o servidor apaga o manual).
//
// Pattern herdado de E6.3.1 (backend): broadcast só dispara em transição
// real (touch retorna changed=True). Heartbeat continuado com mesmo status
// não polui canal — relaxa pressão pra reconciliação em re-render.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getWsUrl } from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  definirStatusManual,
  fetchCompanyPresence,
  type PresenceEntry,
  type PresenceStatus,
  type SettableStatus,
} from "@/services/presenceService";

const HEARTBEAT_INTERVAL_MS = 30_000; // backend TTL 60s — margem 2×
const RECONNECT_INITIAL_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/** Frames server→client emitidos pelo backend (Story E6.3.1). */
type ServerFrame =
  | {
      type: "presence_update";
      user_id: string;
      status: PresenceStatus;
      last_seen: string;
    }
  | { type: "pong" };

interface PresenceContextValue {
  /** Lookup direto de status. Ausente do Map ⇒ `offline`. */
  lookupStatus: (userId: string) => PresenceStatus;
  /** Entry completo (com last_seen) ou null se offline/ainda hidratando. */
  lookupEntry: (userId: string) => PresenceEntry | null;
  /** Status atual do próprio user; `offline` antes do WS abrir. */
  selfStatus: PresenceStatus;
  /**
   * Grava o status escolhido no servidor (vale até a pessoa mudar; `online` = voltar
   * ao automático) e manda um heartbeat. Rejeita se o PUT falhar: quem chamou avisa.
   */
  setStatus: (status: SettableStatus) => Promise<void>;
  /** WS conectado? UI pode mostrar dot cinza-claro quando false. */
  wsConnected: boolean;
}

const PresenceContext = createContext<PresenceContextValue | null>(null);

interface PresenceProviderProps {
  children: React.ReactNode;
}

export function PresenceProvider({ children }: PresenceProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const myUserId = user?.user_id ?? null;

  // Map é state pra trigger re-render em consumers; ref espelha pro WS handler.
  const [entries, setEntries] = useState<Map<string, PresenceEntry>>(new Map());
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const [wsConnected, setWsConnected] = useState(false);

  // Estado "visibility hidden agora": é o único dado que esta aba tem para pedir
  // o status do heartbeat (o manual é do servidor). Nasce do que o navegador diz na
  // hora de montar: uma aba aberta em segundo plano (Ctrl+clique, abas restauradas) não
  // dispara `visibilitychange` e, nascendo "à vista", pediria `online` a cada 30 s até
  // alguém olhar para ela, e o servidor contaria uma aba à vista que ninguém vê.
  const visibilityHiddenRef = useRef(
    typeof document !== "undefined" && document.visibilityState === "hidden"
  );

  const wsRef = useRef<WebSocket | null>(null);
  const heartbeatTimerRef = useRef<number | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectDelayRef = useRef(RECONNECT_INITIAL_MS);
  const shouldReconnectRef = useRef(true);

  // Cauda da fila de setStatus: a promessa do último PUT pedido. Cada setStatus novo
  // espera por ela (ver setStatus).
  const caudaStatusRef = useRef<Promise<unknown>>(Promise.resolve());
  // Quem está na aba AGORA. O token do PUT é lido na hora do pedido: um PUT que esperou
  // na fila não pode sair com o login de quem entrou depois (ver setStatus).
  const usuarioDaAbaRef = useRef(myUserId);
  usuarioDaAbaRef.current = myUserId;

  // ── Hidrate ────────────────────────────────────────────────────────
  // GET company-wide 1× na entrada. Subsequentes broadcasts mantêm sync.
  // Em re-mount (StrictMode dev) chamamos 2× — idempotente.
  useEffect(() => {
    if (!isAuthenticated || !myUserId) return;
    let cancelled = false;
    fetchCompanyPresence()
      .then((res) => {
        if (cancelled) return;
        const map = new Map<string, PresenceEntry>();
        for (const [uid, entry] of Object.entries(res.users)) {
          map.set(uid, entry);
        }
        setEntries(map);
      })
      .catch((err) => {
        // Hydrate falha não é fatal — broadcasts WS preencherão.
        // eslint-disable-next-line no-console
        console.warn("[PresenceContext] hydrate falhou:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, myUserId]);

  // ── Compute status a enviar no heartbeat ───────────────────────────
  // Só o automático: aba escondida → away, visível → online. O Ocupado/Ausente que a
  // pessoa escolheu NÃO vai aqui: o servidor o aplica por cima deste pedido. Assim uma
  // aba nova ou recarregada, que não sabe do manual e pede "online", não o derruba.
  const computeHeartbeatStatus = useCallback((): SettableStatus => {
    return visibilityHiddenRef.current ? "away" : "online";
  }, []);

  // ── Send heartbeat ─────────────────────────────────────────────────
  const sendHeartbeat = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(
      JSON.stringify({
        type: "heartbeat",
        status: computeHeartbeatStatus(),
      })
    );
  }, [computeHeartbeatStatus]);

  // ── WS connection lifecycle ────────────────────────────────────────
  const connectWs = useCallback(() => {
    if (!isAuthenticated || !myUserId) return;

    // Token: pattern oficial do projeto é `seweb_auth` JSON com `access_token`.
    // useChat.ts usa `localStorage.getItem("access_token")` legado — não copiar.
    let token: string | null = null;
    try {
      const raw = localStorage.getItem("seweb_auth");
      if (raw) {
        const parsed = JSON.parse(raw) as { access_token?: string };
        token = parsed.access_token ?? null;
      }
    } catch {
      token = null;
    }
    if (!token) return;

    // Endpoint em /api/v1/ws/presence. Usa getWsUrl() — em prod resolve por
    // window.location.host (same-origin), então o IP NUNCA fica stale quando a
    // VM troca de IP. Mesma lógica dos WS de notifications/feed/maintenance
    // (antes usava import.meta.env.VITE_WS_URL build-time → IP antigo cravado).
    const url = `${getWsUrl("/api/v1/ws/presence")}?token=${encodeURIComponent(token)}`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      reconnectDelayRef.current = RECONNECT_INITIAL_MS;
      // Primeiro heartbeat imediato anuncia presença + setta status real.
      sendHeartbeat();
      // Timer 30s.
      if (heartbeatTimerRef.current) {
        window.clearInterval(heartbeatTimerRef.current);
      }
      heartbeatTimerRef.current = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    };

    ws.onmessage = (ev: MessageEvent) => {
      try {
        const frame = JSON.parse(ev.data) as ServerFrame;
        if (frame.type === "presence_update") {
          setEntries((prev) => {
            const next = new Map(prev);
            if (frame.status === "offline") {
              // Sentinela offline: remove entry. Lookup retorna offline default.
              next.delete(frame.user_id);
            } else {
              next.set(frame.user_id, {
                user_id: frame.user_id,
                status: frame.status,
                last_seen: frame.last_seen,
                // company_id não vem no broadcast — preservamos o do entry
                // anterior; se for primeira aparição, deixamos vazio (a UI
                // não usa este campo, é tracking interno do backend).
                company_id: prev.get(frame.user_id)?.company_id ?? "",
              });
            }
            return next;
          });
        }
        // type:"pong" silently ignored — confirmou keepalive.
      } catch {
        // Frame malformado — ignora.
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      if (heartbeatTimerRef.current) {
        window.clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      if (!shouldReconnectRef.current) return;
      // Exponential backoff: 1s → 2s → 4s → 8s → 16s → 30s (cap)
      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(delay * 2, RECONNECT_MAX_MS);
      reconnectTimerRef.current = window.setTimeout(connectWs, delay);
    };

    ws.onerror = () => {
      // onclose dispara depois — backoff é tratado lá.
    };
  }, [isAuthenticated, myUserId, sendHeartbeat]);

  // Mount WS conexão. Re-monta quando user muda (login/logout/swap).
  useEffect(() => {
    if (!isAuthenticated || !myUserId) return;
    shouldReconnectRef.current = true;
    connectWs();
    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (heartbeatTimerRef.current) {
        window.clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setWsConnected(false);
    };
  }, [isAuthenticated, myUserId, connectWs]);

  // ── visibilitychange → auto-away ───────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    const onVisibility = () => {
      const hidden = document.visibilityState === "hidden";
      visibilityHiddenRef.current = hidden;
      // Manda heartbeat imediato pra refletir transição sem esperar 30s.
      sendHeartbeat();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [isAuthenticated, sendHeartbeat]);

  // ── Public API ──────────────────────────────────────────────────────
  const setStatus = useCallback(
    (status: SettableStatus): Promise<void> => {
      // Em SÉRIE, na ordem dos cliques: dois PUTs soltos quase juntos podem chegar ao
      // servidor (e gravar a cópia do Redis) na ordem inversa, e a escolha mais ANTIGA
      // ficaria valendo em todas as abas. O `catch` é só para a falha de um PUT não
      // travar os seguintes; a promessa de cada chamada continua rejeitando pelo PUT dela.
      const quemClicou = usuarioDaAbaRef.current;
      const minha = caudaStatusRef.current
        .catch(() => undefined)
        .then(async () => {
          // A pessoa saiu (e talvez outra entrou) enquanto este PUT esperava a vez: a
          // escolha era de quem saiu, e o token agora seria o de outra pessoa.
          if (usuarioDaAbaRef.current !== quemClicou) return;
          // Grava primeiro: o heartbeat que vem depois já encontra a escolha no servidor.
          // Falha sobe para quem chamou (o menu avisa); sem heartbeat, nada a refletir.
          await definirStatusManual(status);
          sendHeartbeat();
        });
      caudaStatusRef.current = minha;
      return minha;
    },
    [sendHeartbeat]
  );

  const lookupStatus = useCallback(
    (userId: string): PresenceStatus => {
      return entries.get(userId)?.status ?? "offline";
    },
    [entries]
  );

  const lookupEntry = useCallback(
    (userId: string): PresenceEntry | null => {
      return entries.get(userId) ?? null;
    },
    [entries]
  );

  const selfStatus: PresenceStatus = myUserId
    ? (entries.get(myUserId)?.status ?? "offline")
    : "offline";

  const value = useMemo<PresenceContextValue>(
    () => ({ lookupStatus, lookupEntry, selfStatus, setStatus, wsConnected }),
    [lookupStatus, lookupEntry, selfStatus, setStatus, wsConnected]
  );

  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
}

/**
 * Hook de consumo. Lança se usado fora do Provider — fail-fast em dev,
 * evita "bolinhas que não atualizam" silenciosamente em prod.
 */
export function usePresence(): PresenceContextValue {
  const ctx = useContext(PresenceContext);
  if (!ctx) {
    throw new Error("usePresence() chamado fora de <PresenceProvider> — wrap o componente.");
  }
  return ctx;
}

/**
 * Versão "safe" pra componentes que podem renderizar fora do provider
 * (ex: error boundaries, login screen). Retorna `offline` default.
 */
export function usePresenceSafe(): PresenceContextValue {
  const ctx = useContext(PresenceContext);
  if (!ctx) {
    return {
      lookupStatus: () => "offline",
      lookupEntry: () => null,
      selfStatus: "offline",
      setStatus: async () => undefined,
      wsConnected: false,
    };
  }
  return ctx;
}
